import { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  Badge,
} from 'react-bootstrap';
import {
  FaBook,
  FaEdit,
  FaTrash,
  FaPlus,
  FaKey,
  FaUserLock,
  FaUsers,
  FaSearch,
} from 'react-icons/fa';
import appConstants from '../constants/appConstants';
import Layout from '../components/Layout';
import colors from '../constants/colors';
import { Link } from 'react-router-dom';

const StaffManagement = () => {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const initialFormData = {
    id: '',
    staff_code: '',
    name: '',
    address: '',
    gender: 'Male',
    dob: '',
    mobile: '',
    email: '',
    is_teacher: false,
    pic: '',
    password: '',
  };
  const [formData, setFormData] = useState(initialFormData);

  useEffect(() => {
    fetchStaff();
  }, []);

  const getAuthToken = () => localStorage.getItem('authToken'); // Retrieve token from localStorage

  const fetchStaff = async () => {
    setLoading(true);
    setError('');
    const token = getAuthToken();
    if (!token) {
      setError('No authentication token found');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${appConstants.API_ROOT_URL}get-staff`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (response.ok) {
        setStaff(data.data || []);
      } else {
        setError(data.message || 'Failed to load staff');
      }
    } catch (error) {
      console.error('Failed to fetch staff:', error);
      setError('Failed to load staff. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCloseModal = () => {
    setEditingStaff(null);
    setShowModal(false);
    setFormData(initialFormData);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    const token = getAuthToken();
    if (!token) {
      console.error('No authentication token found');
      return;
    }

    try {
      const response = await fetch(
        `${appConstants.API_ROOT_URL}delete-staff/${id}`,
        {
          method: 'DELETE',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        fetchStaff();
      } else {
        const data = await response.json();
        console.error('Error deleting staff:', data.message);
      }
    } catch (error) {
      console.error('Failed to delete staff:', error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const token = getAuthToken();
    if (!token) {
      alert('Authentication failed. Please log in again.');
      setSaving(false);
      return;
    }

    const url = editingStaff
      ? `${appConstants.API_ROOT_URL}update-staff`
      : `${appConstants.API_ROOT_URL}create-staff`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json(); // Parse response JSON

      if (response.ok) {
        setShowModal(false);
        fetchStaff();
        //alert("Staff member saved successfully.");
      } else {
        console.error('Error saving staff:', data.message || 'Unknown error');
        alert(data.message || 'Failed to save staff. Please try again.');
      }
    } catch (error) {
      console.error('Failed to save staff:', error);
      alert('An unexpected error occurred. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (staff) => {
    setEditingStaff(staff);
    setFormData(staff);
    setShowModal(true);
  };

  const handleShowModal = () => {
    setEditingStaff(null);
    setFormData(initialFormData);
    setShowModal(true);
  };

  // Filter staff based on search term
  const filteredStaff = staff.filter(
    (s) =>
      s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      s.staff_code.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  return (
    <Layout>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Compact Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '10px',
            marginBottom: '12px',
            backgroundColor: colors.sidebar.background,
            padding: '6px 10px',
            borderRadius: '6px',
            color: colors.sidebar.text,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex',
              gap: '8px',
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <span
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                fontWeight: 600,
                fontSize: '1rem',
                color: 'white',
                marginRight: 16,
              }}
            >
              Staff
            </span>

            {/* Search Input */}
            <div style={{ position: 'relative', minWidth: '200px' }}>
              <FaSearch
                style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: colors.sidebar.text,
                  fontSize: '0.9rem',
                }}
              />
              <Form.Control
                size="sm"
                type="text"
                placeholder="Search staff..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{
                  paddingLeft: '35px',
                  height: '30px',
                  backgroundColor: colors.card.background,
                  borderColor: colors.card.border,
                  color: colors.text.primary,
                }}
              />
            </div>
          </div>

          {/* Add Staff Button */}
          <Button
            onClick={handleShowModal}
            variant="light"
            size="sm"
            disabled={saving}
            style={{
              width: '32px',
              height: '28px',
              padding: '0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '6px',
              backgroundColor: colors.button.secondary,
              borderColor: colors.button.secondary,
              color: colors.text.primary,
              fontSize: '0.85rem',
              fontWeight: '600',
            }}
            title="Add Staff"
          >
            <FaPlus />
          </Button>
        </div>

        {/* Error Alert */}
        {error && (
          <Alert
            variant="danger"
            onClose={() => setError('')}
            dismissible
            style={{
              borderRadius: '8px',
              marginBottom: '20px',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Content Section */}
        {loading ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <Spinner
              animation="border"
              style={{ color: '#667eea', width: '50px', height: '50px' }}
            />
            <p style={{ marginTop: '20px', color: '#6c757d' }}>
              Loading staff...
            </p>
          </div>
        ) : filteredStaff.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '60px 20px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            }}
          >
            <FaUsers
              style={{
                fontSize: '4rem',
                color: '#dee2e6',
                marginBottom: '20px',
              }}
            />
            <h4 style={{ color: '#6c757d', marginBottom: '10px' }}>
              No Staff Found
            </h4>
            <p style={{ color: '#adb5bd' }}>
              {searchTerm
                ? 'No staff match your search criteria.'
                : 'Get started by adding your first staff member.'}
            </p>
            {!searchTerm && (
              <Button
                onClick={handleShowModal}
                style={{
                  marginTop: '20px',
                  background:
                    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '10px 24px',
                  borderRadius: '8px',
                  fontWeight: '600',
                }}
              >
                <FaPlus className="me-2" /> Add Staff
              </Button>
            )}
          </div>
        ) : (
          <div
            style={{
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
              overflow: 'hidden',
            }}
          >
            <div style={{ overflowX: 'auto' }}>
              <Table hover style={{ marginBottom: 0 }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: 'white',
                      borderBottom: '2px solid #dee2e6',
                    }}
                  >
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                        width: '80px',
                      }}
                    >
                      #
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                      }}
                    >
                      Staff Details
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'left',
                      }}
                    >
                      Contact
                    </th>
                    <th
                      style={{
                        padding: '16px 20px',
                        fontWeight: '600',
                        color: '#495057',
                        textAlign: 'center',
                        width: '200px',
                      }}
                    >
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredStaff.map((s, index) => (
                    <tr
                      key={s.id}
                      style={{
                        transition: 'background 0.2s ease',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#f0f4ff';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'transparent';
                      }}
                    >
                      <td
                        style={{
                          padding: '14px 20px',
                          color: '#6c757d',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        {index + 1}
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              color: '#2c3e50',
                              fontWeight: '500',
                              fontSize: '0.95rem',
                              marginBottom: '2px',
                            }}
                          >
                            {s.name}
                          </div>
                          <div
                            style={{
                              color: '#6c757d',
                              fontSize: '0.85rem',
                            }}
                          >
                            Code: {s.staff_code}
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div>
                          <div
                            style={{
                              color: '#2c3e50',
                              fontSize: '0.9rem',
                              marginBottom: '2px',
                            }}
                          >
                            {s.email}
                          </div>
                          {s.mobile && (
                            <div
                              style={{
                                color: '#6c757d',
                                fontSize: '0.85rem',
                              }}
                            >
                              {s.mobile}
                            </div>
                          )}
                        </div>
                      </td>
                      <td
                        style={{
                          padding: '14px 20px',
                          textAlign: 'center',
                          borderBottom: '1px solid #e9ecef',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            gap: '4px',
                            justifyContent: 'center',
                            flexWrap: 'wrap',
                          }}
                        >
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleEdit(s)}
                            style={{
                              borderRadius: '6px',
                              padding: '6px 8px',
                              fontSize: '0.8rem',
                            }}
                            title="Edit Staff"
                          >
                            <FaEdit />
                          </Button>
                          <Link to={`/staff-subjects/${s.id}`}>
                            <Button
                              variant="outline-info"
                              size="sm"
                              style={{
                                borderRadius: '6px',
                                padding: '6px 8px',
                                fontSize: '0.8rem',
                              }}
                              title="Subjects"
                            >
                              <FaBook />
                            </Button>
                          </Link>
                          <Link to={`/staff-permission/${s.id}`}>
                            <Button
                              variant="outline-warning"
                              size="sm"
                              style={{
                                borderRadius: '6px',
                                padding: '6px 8px',
                                fontSize: '0.8rem',
                              }}
                              title="Permissions"
                            >
                              <FaUserLock />
                            </Button>
                          </Link>
                          <Link to={`/staff-batch-access/${s.id}`}>
                            <Button
                              variant="outline-success"
                              size="sm"
                              style={{
                                borderRadius: '6px',
                                padding: '6px 8px',
                                fontSize: '0.8rem',
                              }}
                              title="Batch Access"
                            >
                              <FaKey />
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            {/* Summary Footer */}
            <div
              style={{
                padding: '16px 20px',
                background: '#f8f9fa',
                borderTop: '2px solid #dee2e6',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '15px',
              }}
            >
              <span style={{ color: '#6c757d', fontSize: '0.9rem' }}>
                Showing {filteredStaff.length} staff members
                {searchTerm && ' (filtered)'}
              </span>
            </div>
          </div>
        )}
        {/* Add/Edit Staff Modal */}
        <Modal show={showModal} onHide={handleCloseModal} centered size="lg">
          <Modal.Header
            closeButton
            style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderBottom: 'none',
            }}
          >
            <Modal.Title style={{ fontWeight: '700' }}>
              {editingStaff ? 'Edit Staff' : 'Add Staff'}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body style={{ padding: '30px' }}>
            <Form>
              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Staff Code <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.staff_code}
                      onChange={(e) =>
                        setFormData({ ...formData, staff_code: e.target.value })
                      }
                      placeholder="Enter staff code"
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Name <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                      }
                      placeholder="Enter full name"
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Group>
                </div>
              </div>

              <Form.Group className="mb-3">
                <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                  Address
                </Form.Label>
                <Form.Control
                  type="text"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                  placeholder="Enter address"
                  style={{
                    borderRadius: '8px',
                    border: '1px solid #dee2e6',
                    padding: '10px 15px',
                    transition: 'all 0.3s ease',
                  }}
                  onFocus={(e) => {
                    e.currentTarget.style.borderColor = '#667eea';
                    e.currentTarget.style.boxShadow =
                      '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                  }}
                  onBlur={(e) => {
                    e.currentTarget.style.borderColor = '#dee2e6';
                    e.currentTarget.style.boxShadow = 'none';
                  }}
                />
              </Form.Group>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Gender
                    </Form.Label>
                    <Form.Select
                      value={formData.gender}
                      onChange={(e) =>
                        setFormData({ ...formData, gender: e.target.value })
                      }
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </Form.Select>
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Date of Birth
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={formData.dob}
                      onChange={(e) =>
                        setFormData({ ...formData, dob: e.target.value })
                      }
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Group>
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Mobile
                    </Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.mobile}
                      onChange={(e) =>
                        setFormData({ ...formData, mobile: e.target.value })
                      }
                      placeholder="Enter mobile number"
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-6">
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                      Email <span style={{ color: '#dc3545' }}>*</span>
                    </Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.email}
                      onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                      }
                      placeholder="Enter email address"
                      style={{
                        borderRadius: '8px',
                        border: '1px solid #dee2e6',
                        padding: '10px 15px',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.borderColor = '#667eea';
                        e.currentTarget.style.boxShadow =
                          '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.borderColor = '#dee2e6';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    />
                  </Form.Group>
                </div>
              </div>

              {!editingStaff && (
                <Form.Group className="mb-3">
                  <Form.Label style={{ fontWeight: '600', color: '#495057' }}>
                    Password <span style={{ color: '#dc3545' }}>*</span>
                  </Form.Label>
                  <Form.Control
                    type="password"
                    value={formData.password}
                    onChange={(e) =>
                      setFormData({ ...formData, password: e.target.value })
                    }
                    placeholder="Enter password"
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #dee2e6',
                      padding: '10px 15px',
                      transition: 'all 0.3s ease',
                    }}
                    onFocus={(e) => {
                      e.currentTarget.style.borderColor = '#667eea';
                      e.currentTarget.style.boxShadow =
                        '0 0 0 0.2rem rgba(102, 126, 234, 0.1)';
                    }}
                    onBlur={(e) => {
                      e.currentTarget.style.borderColor = '#dee2e6';
                      e.currentTarget.style.boxShadow = 'none';
                    }}
                  />
                </Form.Group>
              )}
            </Form>
          </Modal.Body>
          <Modal.Footer
            style={{ borderTop: '1px solid #dee2e6', padding: '20px 30px' }}
          >
            <Button
              variant="secondary"
              onClick={handleCloseModal}
              disabled={saving}
              style={{
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none',
                borderRadius: '8px',
                padding: '10px 20px',
                fontWeight: '600',
              }}
            >
              {saving ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  Saving...
                </>
              ) : (
                <>{editingStaff ? 'Update Staff' : 'Create Staff'}</>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    </Layout>
  );
};

export default StaffManagement;
